import { NpcReputation } from '../entity/types.js';
/**
 * Reputation values in declaration order (most-positive → most-negative). This
 * order is the single source of truth for "how many steps apart" two reputations
 * are — the windowing logic below and any distance measure derive from it, so
 * there is no second hand-maintained list to keep in sync.
 */
export const REPUTATION_ORDER = Object.values(NpcReputation);
/**
 * One-line description of each reputation, used to render the allowed-reputation
 * window into a prompt so the model understands what each value it may choose
 * actually means.
 */
export const REPUTATION_DESCRIPTIONS = {
    [NpcReputation.ENAMOURED]: 'deeply smitten or captivated by the player',
    [NpcReputation.DEVOTED]: 'loyal and willing to help the player at any cost',
    [NpcReputation.ADORING]: 'reveres and looks up to the player',
    [NpcReputation.GRATEFUL]: 'owes a meaningful debt; warm feelings',
    [NpcReputation.FRIENDLY]: 'generally warm and helpful toward the player',
    [NpcReputation.WARM]: 'positive disposition; approachable',
    [NpcReputation.TRUSTING]: 'comfortable sharing information and secrets',
    [NpcReputation.FOND]: 'likes the player; enjoys their company',
    [NpcReputation.RESPECTFUL]: 'holds the player in esteem',
    [NpcReputation.NEUTRAL]: 'no strong feelings either way',
    [NpcReputation.CURIOUS]: 'interested in the player but uncommitted',
    [NpcReputation.WARY]: 'cautious and watchful, but not yet hostile',
    [NpcReputation.ENVIOUS]: "jealous of the player's fortune or abilities",
    [NpcReputation.SUSPICIOUS]: "doubts the player's motives",
    [NpcReputation.DISTRUSTFUL]: 'does not believe or rely on the player',
    [NpcReputation.CONTEMPTUOUS]: 'looks down on the player',
    [NpcReputation.RESENTFUL]: 'holds a grudge against the player',
    [NpcReputation.FEARFUL]: 'afraid of the player',
    [NpcReputation.HOSTILE]: 'actively antagonistic toward the player',
    [NpcReputation.WRATHFUL]: 'filled with rage and desire for vengeance',
};
/**
 * The reputations reachable from `current` in a single encounter: every value
 * within `maxSteps` positions of `current` in `REPUTATION_ORDER`, clamped to the
 * ends of the scale and **including** `current` itself. Returned most-positive
 * first (i.e. in `REPUTATION_ORDER` order). Including the current value lets a
 * model that echoes it (rather than returning null) express "no change" without
 * being forced by its absence to manufacture a shift to a neighbour.
 */
export function getReputationWindow(current, maxSteps) {
    const i = REPUTATION_ORDER.indexOf(current);
    if (i === -1)
        return [current];
    const start = Math.max(0, i - maxSteps);
    const end = Math.min(REPUTATION_ORDER.length - 1, i + maxSteps);
    return REPUTATION_ORDER.slice(start, end + 1);
}
/**
 * The exchange "deal band" an NPC's reputation falls into, governing which deal
 * shapes are even representable (see feature plan 37). Boundaries are named
 * reputation constants resolved to indices in {@link REPUTATION_ORDER}, so the
 * partition tracks the enum order rather than a hand-maintained list.
 *
 * - **generous** — `FRIENDLY` or better: gifts (either direction), trades, sales.
 * - **transactional** — between `FRIENDLY` and `DISTRUSTFUL` (`warm`…`suspicious`):
 *   value-for-value only — barter, buy, sell, or refusal; never a gift.
 * - **coercive** — `DISTRUSTFUL` or worse: no willing exchange; items move only
 *   under coercion (shakedown / surrender) or not at all.
 */
export const GENEROUS_BAND_FLOOR = NpcReputation.FRIENDLY;
export const COERCIVE_BAND_CEILING = NpcReputation.DISTRUSTFUL;
export function getReputationBand(reputation) {
    const i = REPUTATION_ORDER.indexOf(reputation);
    if (i === -1)
        return 'transactional';
    if (i <= REPUTATION_ORDER.indexOf(GENEROUS_BAND_FLOOR))
        return 'generous';
    if (i >= REPUTATION_ORDER.indexOf(COERCIVE_BAND_CEILING))
        return 'coercive';
    return 'transactional';
}
/**
 * The most-positive reputation still considered actively negative. `WARY` and everything
 * below it (envious, suspicious … wrathful) reads as ill-disposed toward the player;
 * `NEUTRAL` and `CURIOUS` do not. Matches the wary/hostile tone tiers used elsewhere.
 */
export const NEGATIVE_REPUTATION_FLOOR = NpcReputation.WARY;
/** Whether the NPC feels negatively toward the player (`WARY` or worse). */
export function isNegativeReputation(reputation) {
    const i = REPUTATION_ORDER.indexOf(reputation);
    return i !== -1 && i >= REPUTATION_ORDER.indexOf(NEGATIVE_REPUTATION_FLOOR);
}
